// Receiving: scan or pick a PO, count what arrived, post. Scanning an item's barcode here opens the open PO
// it's on and counts it; typing a PO number opens that PO. Everything reads synced data, so it's instant.
// Inbound tracking --- "Arriving this week" (its date within 7 days, or past) comes first, with each PO's carrier and
// tracking number; "Expected later" holds the rest.
import { PackageCheck, ScanLine } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { planToday } from "../../../../shared/day.ts";
import type { Po } from "../../../../shared/db.gen.ts";
import { addPlanDays } from "../../../../shared/planning.ts";
import { PO_STATUS_LABELS } from "../../../../shared/po.ts";
import { arrivalDate } from "../../../../shared/po-followup.ts";
import { matchScan, outstanding } from "../../../../shared/receiving.ts";
import { dayText, qty } from "../../lib/format.ts";
import { navigate, useSearch } from "../../lib/router.tsx";
import { Badge, EmptyState, Input, Thumb } from "../../ui/controls.tsx";
import { toast } from "../../ui/toast.tsx";
import { STATUS_TONE, vendorLabel } from "../po/lib.ts";
import { NeededBy } from "../po/NeededBy.tsx";
import { TrackingText } from "../po/Tracking.tsx";
import { CountPo } from "./CountPo.tsx";
import { beep, hasDraft, linePhoto, parseScan, useReceiving } from "./lib.ts";
import { ReceiptList } from "./ReceiptList.tsx";

export function ReceivingPage() {
  const poId = useSearch().get("po");
  return poId ? <CountPo key={poId} poId={poId} /> : <PickPo />;
}

function PickPo() {
  const r = useReceiving();
  const [text, setText] = useState("");
  const input = useRef<HTMLInputElement>(null);

  const open = (id: string, scan?: string) => navigate(`/receiving?po=${id}${scan ? `&scan=${encodeURIComponent(scan)}` : ""}`);

  // As you type: PO number, vendor, or an item on the PO (SKU, barcode, name).
  const shown = useMemo(() => {
    const want = text.trim().toLowerCase();
    if (!want) return r.open;
    return r.open.filter((p) => {
      const vendor = r.vendorById.get(p.vendor_id);
      if (p.number.toLowerCase().includes(want) || vendor?.name.toLowerCase().includes(want) || vendor?.short_name.toLowerCase().includes(want)) return true;
      return (r.linesByPo.get(p.id) ?? []).some((l) => {
        const item = r.itemById.get(l.item_id);
        return [l.sku, l.vendor_sku, l.name, item?.barcode ?? ""].some((v) => v.toLowerCase().includes(want));
      });
    });
  }, [text, r]);

  // Arriving this week: due within 7 days or already past (late ones first); the rest are expected later.
  const today = planToday();
  const soon = addPlanDays(today, 7);
  const arrives = useMemo(() => new Map(r.open.map((p) => [p.id, arrivalDate(p, r.linesByPo.get(p.id) ?? [])])), [r]);
  const thisWeek = shown.filter((p) => (arrives.get(p.id) ?? "9999") <= soon).sort((a, b) => (arrives.get(a.id) ?? "").localeCompare(arrives.get(b.id) ?? ""));
  const later = shown.filter((p) => !thisWeek.includes(p));

  const submit = () => {
    const raw = text.trim();
    if (!raw) return;
    setText("");
    const number = raw.match(/^(?:po-?)?(\d{3,})$/i);
    if (number) {
      const po = [...r.poById.values()].find((p) => p.number.toUpperCase() === `PO-${number[1]}`);
      if (po && r.open.includes(po)) return open(po.id);
      if (po) {
        beep(false);
        return toast.error(`${po.number} is ${PO_STATUS_LABELS[po.status].toLowerCase()}`);
      }
    }
    // An item: the open PO it's on (the one arriving first), counted once.
    const { code } = parseScan(raw);
    const hits = r.open.filter((p) =>
      matchScan(
        code,
        (r.linesByPo.get(p.id) ?? []).map((l) => ({
          id: l.id,
          barcode: r.itemById.get(l.item_id)?.barcode ?? "",
          sku: l.sku,
          vendor_sku: l.vendor_sku,
          done: outstanding(l) === 0,
        })),
      ),
    );
    if (hits.length) return open(hits[0].id, raw);
    beep(false);
    toast.error(`No open PO for ${raw}`);
    input.current?.focus();
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-4xl px-4 py-6 sm:px-8">
        <h1 className="text-xl font-semibold">Receiving</h1>
        <form
          className="relative mt-4"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <ScanLine className="pointer-events-none absolute top-1/2 left-3 size-5 -translate-y-1/2 text-faint" />
          <Input
            ref={input}
            autoFocus
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Scan an item or type a PO number, vendor or SKU"
            className="h-12 pl-10 text-[15px]"
            aria-label="Scan or search"
          />
        </form>

        {!shown.length ? (
          <>
            <h2 className="mt-6 mb-2 text-[11px] font-medium tracking-wide text-faint uppercase">Expected</h2>
            <div className="rounded-lg border border-line">
              <EmptyState icon={<PackageCheck className="size-8" />} title={text ? "No open PO matches" : "Nothing on its way"}>
                {text ? "Try the PO number from the packing list." : "Sent POs show up here until they're received."}
              </EmptyState>
            </div>
          </>
        ) : text ? (
          <PoGroup title="Expected" pos={shown} r={r} arrives={arrives} today={today} open={open} />
        ) : (
          <>
            {thisWeek.length > 0 && <PoGroup title="Arriving this week" pos={thisWeek} r={r} arrives={arrives} today={today} open={open} />}
            {later.length > 0 && (
              <PoGroup title={thisWeek.length ? "Expected later" : "Expected"} pos={later} r={r} arrives={arrives} today={today} open={open} />
            )}
          </>
        )}

        {r.forBackers.length > 0 && !text && (
          <>
            <h2 className="mt-6 mb-2 text-[11px] font-medium tracking-wide text-faint uppercase">
              At the CM for backers <span className="ml-1">{r.forBackers.length}</span>
            </h2>
            <div className="divide-y divide-line overflow-hidden rounded-lg border border-line">
              {r.forBackers.map(({ po, units }) => (
                <div key={po.id} className="flex items-center gap-3 px-4 py-2.5 text-[13px]">
                  <span className="w-28 shrink-0 font-mono font-semibold">{po.number}</span>
                  <span className="min-w-0 flex-1 truncate text-muted">
                    Nothing more comes to HHL. {vendorLabel(r.vendorById.get(po.vendor_id))} still has {qty(units)} to build for backers; the PO stays open
                    until they're reported built or shipped.
                  </span>
                </div>
              ))}
            </div>
          </>
        )}

        <h2 className="mt-8 mb-2 text-[11px] font-medium tracking-wide text-faint uppercase">Recently received</h2>
        <ReceiptList receipts={r.recent} showPo limit={15} />
      </div>
    </div>
  );
}

function PoGroup({
  title,
  pos,
  r,
  arrives,
  today,
  open,
}: {
  title: string;
  pos: Po[];
  r: ReturnType<typeof useReceiving>;
  arrives: Map<string, string | null>;
  today: string;
  open: (id: string) => void;
}) {
  return (
    <>
      <h2 className="mt-6 mb-2 text-[11px] font-medium tracking-wide text-faint uppercase">
        {title} <span className="ml-1">{pos.length}</span>
      </h2>
      <div className="divide-y divide-line overflow-hidden rounded-lg border border-line">
        {pos.map((p) => {
          const lines = r.linesByPo.get(p.id) ?? [];
          const due = lines.reduce((n, l) => n + outstanding(l), 0);
          const received = lines.some((l) => Number(l.qty_received) > 0);
          const arrive = arrives.get(p.id);
          return (
            // A row, not a <button>: the carrier's tracking link inside it must be a link of its own.
            // biome-ignore lint/a11y/useSemanticElements: a <button> can't hold the tracking link
            <div
              key={p.id}
              role="button"
              tabIndex={0}
              onClick={() => open(p.id)}
              onKeyDown={(e) => {
                if (e.key !== "Enter" && e.key !== " ") return;
                e.preventDefault();
                open(p.id);
              }}
              className="flex w-full cursor-pointer items-center gap-3 px-4 py-3 text-left hover:bg-hover"
            >
              <div className="flex w-28 shrink-0 flex-col">
                <span className="font-mono text-[13px] font-semibold">{p.number}</span>
                <span className="truncate text-[12px] text-muted">{vendorLabel(r.vendorById.get(p.vendor_id))}</span>
              </div>
              <div className="flex min-w-0 flex-1 flex-col gap-1 overflow-hidden">
                <div className="flex min-w-0 items-center gap-1 overflow-hidden">
                  {lines.slice(0, 8).map((l) => (
                    <Thumb key={l.id} src={linePhoto(l, r.itemById.get(l.item_id))} size={36} />
                  ))}
                  {lines.length > 8 && <span className="ml-1 text-[12px] text-faint">+{lines.length - 8}</span>}
                </div>
                <TrackingText po={p} className="text-[12px] text-muted" />
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1">
                <span className="flex items-center gap-1">
                  {hasDraft(p.id) && <Badge tone="accent">Counting</Badge>}
                  {received && <Badge tone="warn">Part received</Badge>}
                  <Badge tone={STATUS_TONE[p.status]}>{PO_STATUS_LABELS[p.status]}</Badge>
                </span>
                <span className="text-[12px] text-muted">
                  {lines.length} {lines.length === 1 ? "line" : "lines"} · {qty(due)} due
                  {arrive && arrive !== String(p.expected_arrive ?? "").slice(0, 10) ? (
                    <span className={arrive < today ? "text-danger" : ""}> · arrives {dayText(arrive)}</span>
                  ) : (
                    <NeededBy po={p} />
                  )}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
